import { Provider as RollbarProvider } from '@rollbar/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { domNormalize } from 'common/utils/domNormalizeHack'
import { shouldForwardProp } from 'common/utils/should-forward-prop'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { initializeCommonHttpClientInterceptors } from 'src/editor/api/http-clients/common-http-client'
import { initializeMonolithHttpClientInterceptors } from 'src/editor/api/http-clients/monolith-http-client'
import { initializePageEditorHttpClientInterceptors } from 'src/editor/api/http-clients/page-editor-http-client'
import { initializeWebsiteHttpClientInterceptors } from 'src/editor/api/http-clients/website-http-client'
import 'src/editor/sentry'
import {
  extensionProtocols,
  ignoredMessages,
} from 'src/editor/sentry/constants'
import { queryClient } from 'src/editor/shared/utils/queryClient'
import { StyleSheetManager } from 'styled-components'
import App from './App'
import { i18nInitPromise } from './i18n'
import { configureStore } from './store/index'
import './styles.css'

const enableMocking = async () => {
  if (process.env.MOCK_ENABLED !== 'true') {
    return
  }
  const { worker } = await import('src/shared/mocks/msw/browser')
  await worker.start({
    serviceWorker: { url: '/mockServiceWorker.js' },
    onUnhandledRequest: 'warn',
  })
}

domNormalize()

const isProdEnv = process.env.NODE_ENV === 'production'

initializeCommonHttpClientInterceptors()
initializeWebsiteHttpClientInterceptors()
initializePageEditorHttpClientInterceptors()
initializeMonolithHttpClientInterceptors()

const store = configureStore()
const root = createRoot(document.getElementById('app'))

const rollbarConfig = {
  enabled: isProdEnv,
  captureUncaught: true,
  accessToken: isProdEnv && process.env.ROLLBAR_EDITOR_KEY,
  captureUnhandledRejections: true,
  environment: process.env.APP_ENV,
  ignoredMessages,
  checkIgnore: (_isUncaught, _args, payload) => {
    // Ignore errors originating from browser extensions (e.g., MetaMask)
    const trace = payload?.body?.trace
    const traceChain = payload?.body?.trace_chain

    const collectFrames = () => {
      if (trace?.frames) {
        return trace.frames
      }
      if (Array.isArray(traceChain)) {
        return traceChain.flatMap(t => t?.frames || [])
      }
      return []
    }

    const frames = collectFrames()

    return frames.some(f =>
      extensionProtocols.some(p => f?.filename?.startsWith(p)),
    )
  },
  autoInstrument: true,
  payload: {
    host: process.env.SYSTEME_IO_HOST,
    client: {
      javascript: {
        source_map_enabled: true, // this is now true by default
        code_version: isProdEnv && process.env.CODE_VERSION,
        // Optionally have Rollbar guess which frames the error was thrown from
        // when the browser does not provide line and column numbers.
        guess_uncaught_frames: true,
      },
    },
  },
  filterTelemetry: e =>
    e.type === 'manual' &&
    e.body &&
    e.body.type &&
    e.body.type.startsWith('FETCH'),
}

Promise.all([enableMocking(), i18nInitPromise]).then(() => {
  root.render(
    <RollbarProvider config={rollbarConfig}>
      <Provider store={store}>
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
          <QueryClientProvider client={queryClient}>
            <App />
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </QueryClientProvider>
        </StyleSheetManager>
      </Provider>
    </RollbarProvider>,
  )
})
